// 保存済みデータ（三浦さん提案 2026-09-01）
//
// 「作業している場所」と「保存されている場所」が画面上で分かれて見えないと、
// 確定したはずの月がどこに残っているのか分からず不安になる（本城さんの 9/1 の質問がまさにそれ）。
// この画面は、サーバに保存済みの月だけを一覧し、そこから読み取り専用で開けるようにする。

import React, { useEffect, useState } from "react";
import { Ic } from "../components/Icons";
import { useStore } from "../store";
import { yen } from "../lib/normalize";

function formatMonth(ym: string): string {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split("-").map(Number);
  return `${y}年${m}月`;
}

function formatStamp(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("ja-JP");
}

export function SavedMonthsScreen({ onNav }: { onNav: (s: string) => void }) {
  const { state, monthIndex, refreshMonthIndex, viewMonth, exitArchive, archiveBusy, monthLocked } = useStore();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      await refreshMonthIndex();
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // いま作業している月が保存済みか（＝この一覧に載っているか）
  const workingSaved = monthIndex.find((e) => e.month === state.targetMonth) || null;

  const openMonth = async (month: string) => {
    setError(null);
    if (month === state.targetMonth) {
      // 作業中の月は読み取り専用にせず、そのまま一覧へ
      exitArchive();
      onNav("list");
      return;
    }
    const r = await viewMonth(month);
    if (!r.ok) {
      setError(r.error || "この月のデータを開けませんでした");
      return;
    }
    onNav("list");
  };

  return (
    <div className="content" style={{ maxWidth: 1100 }}>
      <div className="page-title-row">
        <div className="col gap-4">
          <div className="page-title">保存済みデータ</div>
          <div className="page-sub muted">
            確定した月はサーバに保存され、編集できなくなります。ここから開くと、内容の確認・PDF再出力ができます。
            直す必要があるときは、開いた先の画面で「この月を訂正する」を押してください。
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={() => onNav("home")}><Ic.chevronL size={13} /> ホーム</button>
        <button className="btn" onClick={reload} disabled={loading || archiveBusy}>
          <Ic.history size={14} /> 更新
        </button>
      </div>

      {error && (
        <div className="toast warn">
          <Ic.alert size={14} />
          <div>{error}</div>
        </div>
      )}

      {/* いま作業している月：保存されているかどうかをはっきり出す */}
      <div className="card">
        <div className="card-head">
          <div className="card-title">いま開いている月</div>
        </div>
        <div className="card-pad row gap-12" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <div className="col gap-4" style={{ minWidth: 200 }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{formatMonth(state.targetMonth)}</div>
            <div className="tiny muted">明細 {state.statements.length} 件</div>
          </div>
          <div style={{ flex: 1 }} />
          {workingSaved ? (
            <div className="col gap-4" style={{ alignItems: "flex-end" }}>
              {monthLocked
                ? <span className="chip green dot">確定済み・編集不可</span>
                : <span className="chip amber dot">訂正中（編集できます）</span>}
              <span className="tiny muted">確定 {formatStamp(workingSaved.finalizedAt)} ・ {workingSaved.finalizedBy || "—"}</span>
            </div>
          ) : (
            <div className="col gap-4" style={{ alignItems: "flex-end" }}>
              <span className="chip amber dot">未保存</span>
              <span className="tiny muted">
                {state.statements.length > 0
                  ? "支払明細 一覧の「月を確定」でサーバに保存されます"
                  : "CSVを取り込むとここに件数が出ます"}
              </span>
            </div>
          )}
          <button className="btn" onClick={() => openMonth(state.targetMonth)}>
            この月を開く <Ic.arrowR size={14} />
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="card-title">保存済みの月（{monthIndex.length} か月）</div>
        </div>
        {monthIndex.length === 0 ? (
          <div className="card-pad muted tiny">
            {loading ? "読み込み中…" : "まだ保存された月はありません。支払明細 一覧で全件を確定したあと「月を確定」を押すと、この一覧に入ります。"}
          </div>
        ) : (
          <div className="tbl-scroll tbl-cards-wrap">
            <table className="tbl tbl-cards">
              <thead>
                <tr>
                  <th>月</th>
                  <th>確定日時</th>
                  <th>確定者</th>
                  <th style={{ textAlign: "right" }}>件数</th>
                  <th style={{ textAlign: "right" }}>売上合計</th>
                  <th style={{ textAlign: "right" }}>控除合計</th>
                  <th style={{ textAlign: "right" }}>差引支給合計</th>
                  <th style={{ width: 96 }}></th>
                </tr>
              </thead>
              <tbody>
                {monthIndex.map((e) => (
                  <tr key={e.month}>
                    <td className="card-title-cell" style={{ fontWeight: 700 }}>
                      {formatMonth(e.month)}
                      {e.month === state.targetMonth && (
                        monthLocked
                          ? <span className="chip green" style={{ marginLeft: 6 }} title="確定済みです。直すには開いた先の画面で「この月を訂正する」を押してください">確定済み</span>
                          : <span className="chip amber" style={{ marginLeft: 6 }} title="訂正中です。編集でき、「月を確定」で保存し直すと再び確定済みになります">訂正中</span>
                      )}
                    </td>
                    <td className="tiny" data-label="確定日時">{formatStamp(e.finalizedAt)}</td>
                    <td className="tiny" data-label="確定者">{e.finalizedBy || "—"}</td>
                    <td data-label="件数" style={{ textAlign: "right" }}>{e.totals.count.toLocaleString("ja-JP")}</td>
                    <td data-label="売上合計" style={{ textAlign: "right" }}>{yen(e.totals.salesTotal)}</td>
                    <td data-label="控除合計" style={{ textAlign: "right" }}>△{yen(e.totals.deductTotal)}</td>
                    <td className="net-cell" data-label="差引支給合計" style={{ textAlign: "right", fontWeight: 700 }}>{yen(e.totals.netTotal)}</td>
                    <td>
                      <button className="btn sm" onClick={() => openMonth(e.month)} disabled={archiveBusy}>
                        {e.month === state.targetMonth ? "開く" : "読み取りで開く"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="card-pad tiny muted">
          確定した月は編集できません。直す必要があるときは、その月を開いた先の画面で「この月を訂正する」（過去月は「この月を訂正（再オープン）」）を押してください。
          <br />
          過去月で1人だけ直すときは、その方の明細を開いて「この人だけ訂正」を押してください。作業中の月と、同じ月の他の方には影響しません。
          <br />
          「訂正中」と付いている月は、いま編集できる状態です。直したあと「月を確定」を押すと、保存し直して再び確定済みになります。
        </div>
      </div>
    </div>
  );
}
